import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { v4 as uuid } from 'uuid';
import * as api from '../../../../services/api/gdprApi';

function errorMessage(error) {
    return error?.response?.data?.code === 'RECENT_LOGIN_REQUIRED'
        ? 'Logga ut och logga in igen innan du hanterar personuppgifterna.'
        : error?.response?.status === 403 ? 'Du behöver behörighet att administrera personal i detta företag.'
        : error?.response?.data?.message || 'Åtgärden kunde inte slutföras. Försök igen.';
}
export default function EmployeePrivacyActions({ companyId, employeeId, onRedacted }) {
    const [show, setShow] = useState(false);
    const [preview, setPreview] = useState(null);
    const [selected, setSelected] = useState([]);
    const [reason, setReason] = useState('');
    const [reviewAt, setReviewAt] = useState('');
    const [confirmed, setConfirmed] = useState(false);
    const [requestId, setRequestId] = useState(uuid);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const generation = useRef(0);
    useEffect(() => {
        const current = ++generation.current; setShow(false); setPreview(null); setBusy(false);
        return () => { generation.current = current + 1; };
    }, [companyId, employeeId]);
    const open = async () => {
        const current = generation.current;
        setShow(true); setPreview(null); setSelected([]); setConfirmed(false); setReason(''); setReviewAt('');
        setError(''); setMessage(''); setRequestId(uuid()); setBusy(true);
        try { const data = (await api.previewEmployeeErasure(companyId, employeeId)).data.data; if (current === generation.current) setPreview(data); }
        catch (failure) { if (current === generation.current) setError(errorMessage(failure)); }
        finally { if (current === generation.current) setBusy(false); }
    };
    const exportData = async () => {
        const current = generation.current;
        setBusy(true); setError(''); setMessage('');
        try {
            const { data } = (await api.exportEmployeeData(companyId, employeeId)).data;
            if (current !== generation.current) return;
            const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
            const anchor = document.createElement('a'); anchor.href = url;
            anchor.download = `personaluppgifter-${employeeId}.json`; anchor.click(); URL.revokeObjectURL(url);
            setMessage('Sammanställningen har laddats ner. Kontrollera täckning, dokumentbilagor och andra personers uppgifter innan den lämnas ut.');
        } catch (failure) { if (current === generation.current) setError(errorMessage(failure)); }
        finally { if (current === generation.current) setBusy(false); }
    };
    const redact = async () => {
        if (!confirmed || !selected.length || !reason.trim() || !reviewAt) return;
        const current = generation.current;
        setBusy(true); setError(''); setMessage('');
        try {
            const { data } = (await api.redactEmployee(companyId, employeeId, { confirm: true,
                categories: selected, reason, recordIntegrityReviewed: confirmed, reviewAt, requestId })).data;
            if (current !== generation.current) return;
            if (!data.redacted || data.anonymized !== false) throw new Error('Unexpected redaction result');
            setMessage('Valda uppgifter har rensats och beslutet har registrerats. Personen är inte anonymiserad; bevarade handlingar och kopplings-ID finns kvar.');
            setConfirmed(false); setSelected([]); setRequestId(uuid()); setPreview(null);
            onRedacted?.();
        } catch (failure) { if (current === generation.current) setError(errorMessage(failure)); }
        finally { if (current === generation.current) setBusy(false); }
    };
    return <>
        <Button variant="outline-secondary" onClick={open}>Personuppgifter</Button>
        <Modal show={show} onHide={busy ? undefined : () => setShow(false)} size="lg">
            <Modal.Header closeButton={!busy}><Modal.Title>Hantera personuppgifter</Modal.Title></Modal.Header>
            <Modal.Body>
                <p>Företaget bedömer vad som kan lämnas ut, raderas och behöver bevaras. Det personliga LedgerLoop-kontot hanteras separat.</p>
                <Button variant="outline-primary" onClick={exportData} disabled={busy || !preview}>Ladda ner sammanställning</Button>
                {preview && <>
                    <h2 className="h5 mt-4">Rensa valda uppgifter</h2>
                    <Alert variant="warning">Rensning kan inte ångras. Löne- och bokföringsunderlag ändras inte av denna åtgärd.
                        Kontrollera att uppgifterna du väljer kan tas bort utan att skada handlingar som ska bevaras.</Alert>
                    {preview.employeeStatus === 'active' && <Alert variant="info">Avsluta eller arkivera anställningen innan du rensar dessa uppgifter.</Alert>}
                    {preview.categories.map(category => <Form.Group key={category.key} className="mb-3">
                        <Form.Check id={`erase-${category.key}`} type="checkbox" label={category.label}
                            disabled={busy || !!category.blockedReason || preview.employeeStatus === 'active'} checked={selected.includes(category.key)}
                            onChange={e => { const checked = e.target.checked; setRequestId(uuid()); setSelected(values => checked ? [...values, category.key] : values.filter(key => key !== category.key)); }} />
                        <small>{category.blockedReason || category.scope}</small>
                    </Form.Group>)}
                    <Form.Group controlId="employee-erasure-reason" className="mb-3">
                        <Form.Label>Skäl och bedömning</Form.Label>
                        <Form.Control as="textarea" rows={2} maxLength={500} value={reason} disabled={busy}
                            onChange={e => { setReason(e.target.value); setRequestId(uuid()); }} />
                    </Form.Group>
                    <Form.Group controlId="employee-erasure-review" className="mb-3">
                        <Form.Label>När ska kvarvarande uppgifter granskas igen?</Form.Label>
                        <Form.Control type="date" value={reviewAt} disabled={busy} onChange={e => { setReviewAt(e.target.value); setRequestId(uuid()); }} />
                    </Form.Group>
                    <Form.Check id="employee-erasure-confirm" type="checkbox" checked={confirmed} disabled={busy}
                        onChange={e => setConfirmed(e.target.checked)} label="Jag har kontrollerat bevarandebehovet och bekräftar rensning av de valda uppgifterna." />
                    <h2 className="h5 mt-4">Kvar att bedöma</h2>
                    <ul>{preview.retained.map(item => <li key={item.key}><strong>{item.label}:</strong> {item.reason}</li>)}</ul>
                    {!!preview.decisions?.length && <><h2 className="h5 mt-4">Tidigare beslut</h2>
                        <ul>{preview.decisions.map(decision => <li key={decision.requestId}>{decision.reason} —
                            uppföljning {new Date(decision.reviewAt).toLocaleDateString('sv-SE')}{decision.reviewDue ? ' (behöver följas upp)' : ''}</li>)}</ul>
                    </>}
                </>}
                {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                {message && <Alert variant="info" className="mt-3">{message}</Alert>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" disabled={busy} onClick={() => setShow(false)}>Stäng</Button>
                <Button variant="danger" onClick={redact} disabled={busy || !preview || preview.employeeStatus === 'active' || !selected.length || !confirmed || !reason.trim() || !reviewAt}>
                    {busy ? 'Arbetar…' : 'Rensa valda uppgifter'}
                </Button>
            </Modal.Footer>
        </Modal>
    </>;
}
